import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useFavorites } from "@/hooks/useFavorites";
import { formatCount, getCategoryById, getWorkById } from "@/utils/gallery";

export default function FavoritesPage() {
  const { favorites, removeFavorite, clearFavorites } = useFavorites();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const favoriteWorks = useMemo(
    () => favorites.map((id) => getWorkById(id)).filter((work) => work !== undefined),
    [favorites]
  );

  const handleClear = () => {
    clearFavorites();
    setConfirmOpen(false);
  };

  return (
    <div className="px-4 pb-20 pt-24 md:px-8 md:pb-28 md:pt-28">
      <div className="mb-6">
        <Breadcrumb items={[{ label: "我的收藏" }]} />
      </div>

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">我的收藏</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            收藏的作品会保存在当前浏览器本地，无需登录即可使用。你可以随时移除单件作品或清空全部收藏。
          </p>
        </div>
        {favoriteWorks.length > 0 && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
          >
            <i className="ri-delete-bin-6-line" />
            清空全部收藏
          </button>
        )}
      </div>

      {favoriteWorks.length > 0 ? (
        <>
          <p className="mb-5 text-sm text-foreground-600">
            已收藏 <span className="font-semibold text-foreground-950">{favoriteWorks.length}</span> 组作品
          </p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {favoriteWorks.map((work) => {
              const category = getCategoryById(work.categoryId);
              return (
                <div
                  key={work.id}
                  className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <Link to={`/detail/${work.id}`} className="block">
                    <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-100">
                      <img
                        src={work.cover}
                        alt={`${work.title} - 收藏的模特写真作品`}
                        title={`${work.title}写真作品`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />
                      {category && (
                        <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs text-foreground-900">
                          {category.name}
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 p-4">
                        <h2 className="text-base font-semibold text-white">{work.title}</h2>
                        <p className="mt-1 text-xs text-white/80">
                          模特 {work.model} · {work.date}
                        </p>
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center justify-between px-4 py-3 text-xs text-foreground-600">
                    <span className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {formatCount(work.views)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-3-line text-accent-500" />
                        {formatCount(work.likes)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFavorite(work.id)}
                      className="flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-accent-600 transition-colors hover:bg-accent-100"
                    >
                      <i className="ri-heart-3-fill" />
                      取消收藏
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-heart-3-line text-3xl" />
          </div>
          <h2 className="mt-5 text-base font-semibold text-foreground-950">您还没有收藏任何作品</h2>
          <p className="mt-2 max-w-sm text-sm text-foreground-500">
            浏览作品时点击「收藏作品」按钮，即可把喜欢的写真作品保存在这里。
          </p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-compass-3-line" />
            去发现作品
          </Link>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-error-warning-line text-2xl" />
            </div>
            <h2 className="mt-4 text-base font-semibold text-foreground-950">确认清空全部收藏？</h2>
            <p className="mt-2 text-sm text-foreground-500">
              清空后将移除全部 {favoriteWorks.length} 组收藏作品，此操作不可撤销。
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="flex-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}